import {
  ArrowLeftIcon,
  CalendarIcon,
  ClockIcon,
  DocumentTextIcon,
  MapPinIcon,
  PencilSquareIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { Button, Link, Spinner } from '@heroui/react';
import { skipToken } from '@reduxjs/toolkit/query';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import StaffAvailabilityList from '@/app/components/staff-availability-list.tsx';
import { formatDate } from '@/app/helpers/date.helpers.ts';
import { useAuth } from '@/app/hooks/auth-provider';
import { useDebounce } from '@/app/hooks.ts';
import toast from '@/app/toast.ts';
import { Divider } from '@/primitives/divider.tsx';
import { Heading, Subheading } from '@/primitives/heading.tsx';
import { Text } from '@/primitives/text.tsx';
import {
  useFetchAvailabilitySlotQuery,
  usePutAvailabilitySlotMutation,
} from '@/slices/availability-slot-api-slice.ts';
import { useFetchStaffMembersQuery } from '@/slices/staff-api-slice.ts';
import { useSecurityContext } from '../../../hooks/security-provider.tsx';
import type {
  AvailabilitySlotPutModel,
  StaffMemberAvailabilityModel,
} from '../../../types/availability-slot.type.ts';
import { ConfirmationStatusEnum } from '../../../types/confirmation-status.model.ts';
import { hasPermission } from '../../../types/constants.ts';
import { type StaffMemberModel, StaffRoleEnum } from '../../../types/staff-member.type.ts';
import { UserTypeEnum } from '../../../types/user-type.ts';
import { sortStaffMembers } from './helpers-availability.ts';

const DetailsAvailability = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentAccount, setCurrentAccount, visibleAccounts } = useSecurityContext();
  const { data } = useFetchAvailabilitySlotQuery(params?.key ?? skipToken);

  const userCanUpdateStaffAvailability =
    user?.userType === UserTypeEnum.Admin ||
    (user?.userType === UserTypeEnum.StaffMember &&
      hasPermission(user as StaffMemberModel, StaffRoleEnum.SiteAdministrator, data?.accountId));

  const { data: accountStaffMembers, isLoading: staffMembersLoading } = useFetchStaffMembersQuery(
    userCanUpdateStaffAvailability && data?.accountKey
      ? `accountKey=${data?.accountKey}`
      : skipToken,
  );
  const [putAvailabilitySlot] = usePutAvailabilitySlotMutation();

  const [isSaving, setIsSaving] = useState(false);
  const sharedMealHeadcount = currentAccount?.mealHeadcount;
  const sharedMealInstructions = currentAccount?.mealInstructions;
  const sharedMealHeadcountText = sharedMealHeadcount ? sharedMealHeadcount.toString() : null;

  // This set function is "overridden" to filter and sort staff members. It is called from an effect.
  const [staffMemberAvailabilities, _setStaffMemberAvailabilities] = useState<
    (StaffMemberAvailabilityModel & { pendingUpdate?: boolean })[]
  >([]);
  useEffect(() => {
    if (data?.accountKey && data.accountKey !== currentAccount?.key) {
      const newAccount = visibleAccounts?.find(x => x.key === data?.accountKey);
      setCurrentAccount(newAccount);
    }
  }, [data, currentAccount]);

  function setStaffMemberAvailabilities(
    newStaffMemberAvailabilities: (typeof staffMemberAvailabilities)[0][],
  ) {
    // filter out unconfirmed staff for representative users (or other users who can't update staff availability)
    const filteredStaffMemberAvailabilities = userCanUpdateStaffAvailability
      ? newStaffMemberAvailabilities
      : newStaffMemberAvailabilities.filter(
          x =>
            x.confirmationStatus &&
            x.confirmationStatus.confirmationStatusId !== ConfirmationStatusEnum.Unconfirmed,
        );

    // sort staff members based on default sorting rules
    const sortedStaffMemberAvailabilities = sortStaffMembers(filteredStaffMemberAvailabilities);

    _setStaffMemberAvailabilities(sortedStaffMemberAvailabilities);
  }

  useEffect(() => {
    const staffMemberAvailabilities = data?.staffMemberAvailabilities
      ? [...data.staffMemberAvailabilities]
      : [];

    // for admins: ensure all staff members are in the list, even if they don't have an availability yet
    if (accountStaffMembers) {
      for (const accountStaffMember of accountStaffMembers) {
        if (staffMemberAvailabilities?.some(x => x.staffMember.id === accountStaffMember.id)) {
          continue;
        }

        const accountSiteStaffMember = accountStaffMember.accountSiteStaffMembers.find(
          x => x.accountId === data?.accountId,
        );

        if (!accountSiteStaffMember) {
          continue;
        }

        staffMemberAvailabilities.push({
          staffMember: accountStaffMember,
          availabilitySlotId: data?.id,
          confirmationStatus: {
            confirmationStatusId: ConfirmationStatusEnum.Unconfirmed,
            description: 'Unconfirmed',
          },
        });
      }
    }

    setStaffMemberAvailabilities(staffMemberAvailabilities);
  }, [data, accountStaffMembers]);

  // set staff member availability status as pending update
  function setStaffMemberAvailabilityStatus(
    staffMemberId: number,
    confirmationStatusId: ConfirmationStatusEnum,
  ) {
    if (!userCanUpdateStaffAvailability) {
      return;
    }

    const savedStaffMemberAvailability = data?.staffMemberAvailabilities.find(
      x => x.staffMember.id === staffMemberId,
    );

    const savedConfirmationStatusId =
      savedStaffMemberAvailability?.confirmationStatus?.confirmationStatusId ??
      ConfirmationStatusEnum.Unconfirmed;

    if (confirmationStatusId === savedConfirmationStatusId) {
      return;
    }

    const updatedStaffMemberAvailabilities = [
      ...staffMemberAvailabilities.filter(x => x.staffMember.id !== staffMemberId),
      {
        ...staffMemberAvailabilities.find(x => x.staffMember.id === staffMemberId),
        confirmationStatus: {
          confirmationStatusId,
          description: ConfirmationStatusEnum[confirmationStatusId].toString(),
        },
        pendingUpdate: confirmationStatusId !== savedConfirmationStatusId,
      },
    ];

    setStaffMemberAvailabilities(updatedStaffMemberAvailabilities);
  }

  // save staff member availability updates to server
  async function saveStaffMemberAvailabilityUpdates() {
    if (!staffMemberAvailabilities.some(x => x.pendingUpdate)) {
      return;
    }

    const staffMemberAvailabilitiesMap = staffMemberAvailabilities.reduce<{
      [staffMemberId: number]: ConfirmationStatusEnum;
    }>((map, staffMemberAvailability) => {
      map[staffMemberAvailability.staffMember.id] =
        staffMemberAvailability.confirmationStatus?.confirmationStatusId;
      return map;
    }, {});

    const model: AvailabilitySlotPutModel = {
      start: data.start,
      end: data.end,
      officeNotes: data.officeNotes,
      accountMeetingLocationId: data.accountMeetingLocation.id,
      accountRepresentativeIds: data.accountRepresentatives.map(x => x.id),
      accountCategoryIds: data.accountCategories.map(x => x.id),
      staffMemberAvailabilities: staffMemberAvailabilitiesMap,
    };

    setIsSaving(true);
    try {
      await putAvailabilitySlot({ key: data.key, model }).unwrap();
      toast.success('Staff member availability status updated successfully.');
    } catch {
      toast.error('There was an error updating the staff member availability status.');
    } finally {
      setIsSaving(false);
    }
  }

  const debouncedStaffMemberAvailabilities = useDebounce(staffMemberAvailabilities, 2000);

  // save staff member availabilities on change
  useEffect(() => {
    saveStaffMemberAvailabilityUpdates();
  }, [debouncedStaffMemberAvailabilities]);

  // save staff member availabilities on unmount
  useEffect(() => {
    return () => {
      saveStaffMemberAvailabilityUpdates();
    };
  }, []);

  return (
    <div className='flex flex-col gap-6'>
      <div className='flex items-center gap-4'>
        <Button color='default' variant='light' onPress={() => navigate(-1)}>
          <ArrowLeftIcon className='size-5' />
          Back
        </Button>
      </div>

      <div className='flex items-center gap-2 justify-between'>
        <Heading icon={<CalendarIcon className='size-7' />}>Availability Details</Heading>
        <div>
          {(user?.userType === UserTypeEnum.Admin ||
            (user?.userType === UserTypeEnum.StaffMember &&
              hasPermission(
                user as StaffMemberModel,
                StaffRoleEnum.SiteAdministrator,
                data?.accountId,
              ))) && (
            <Button color='primary' as={Link} href={`/availability/edit/${data?.key}`}>
              <PencilSquareIcon className='size-5' />
              Edit Availability
            </Button>
          )}
        </div>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-5 gap-6'>
        <div className='bg-white rounded-xl shadow-md border border-gray-300 p-6 col-span-2'>
          <Subheading className='mb-6'>Basic Details</Subheading>
          <div className='space-y-4'>
            <div className='flex items-start gap-3'>
              <CalendarIcon className='size-5 text-primary-600 shrink-0' />
              <div className='flex flex-col'>
                {data?.start ? (
                  <Text className='font-medium'>
                    <time dateTime={data.start}>{formatDate(data.start, DateTime.DATE_FULL)}</time>
                  </Text>
                ) : (
                  <Text className='text-gray-400 italic'>No date set</Text>
                )}
              </div>
            </div>

            <div className='flex items-start gap-3'>
              <ClockIcon className='size-5 text-primary-600 shrink-0' />
              <div className='flex flex-col'>
                {data?.start && data?.end ? (
                  <Text className='font-medium'>
                    <time dateTime={data.start}>
                      {formatDate(data.start, DateTime.TIME_SIMPLE)}
                    </time>
                    &nbsp;-&nbsp;
                    <time dateTime={data.end}>{formatDate(data.end, DateTime.TIME_SIMPLE)}</time>
                  </Text>
                ) : (
                  <Text className='text-gray-400 italic'>No time set</Text>
                )}
              </div>
            </div>

            <div className='flex items-start gap-3'>
              <MapPinIcon className='size-5 text-primary-600 shrink-0' />
              <div className='flex flex-col'>
                {data?.accountMeetingLocation?.description ? (
                  <Text className='font-medium'>{data.accountMeetingLocation.description}</Text>
                ) : (
                  <Text className='text-gray-400 italic'>Room not assigned</Text>
                )}
              </div>
            </div>

            <div className='flex items-start gap-3'>
              <DocumentTextIcon className='size-5 text-primary-600 shrink-0' />
              <div className='flex flex-col flex-1'>
                {data?.officeNotes ? (
                  <Text className='font-medium'>{data.officeNotes}</Text>
                ) : (
                  <Text className='text-gray-400 italic'>No instructions provided</Text>
                )}
              </div>
            </div>

            <div className='flex items-start gap-3'>
              <UserGroupIcon className='size-5 text-primary-600 shrink-0' />
              <div className='flex flex-col flex-1'>
                <Text className='font-medium'>
                  Headcount:{' '}
                  {sharedMealHeadcountText ? (
                    sharedMealHeadcountText
                  ) : (
                    <span className='text-gray-400 italic'>--</span>
                  )}
                </Text>
                <Text className='text-sm text-gray-600 mt-1'>
                  Instructions:{' '}
                  {sharedMealInstructions || <span className='text-gray-400 italic'>--</span>}
                </Text>
              </div>
            </div>
          </div>

          {user?.userType !== UserTypeEnum.DrugRep && (
            <>
              <Divider className='my-6' />
              <Subheading className='mb-4'>Rep Access</Subheading>
              <div className='space-y-4'>
                <div>
                  <Text className='text-xs text-gray-500 mb-2'>Categories</Text>
                  {data?.accountCategories && data.accountCategories.length > 0 ? (
                    <div className='space-y-1'>
                      {data.accountCategories.map(value => (
                        <div key={value.id} className='flex items-center gap-2'>
                          <span className='text-primary-600'>•</span>
                          <Text>{value.description}</Text>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Text className='text-gray-400 italic'>No categories assigned</Text>
                  )}
                </div>

                <div>
                  <Text className='text-xs text-gray-500 mb-2'>Representatives</Text>
                  {data?.accountRepresentatives && data.accountRepresentatives.length > 0 ? (
                    <div className='space-y-1'>
                      {data.accountRepresentatives.map(value => (
                        <div key={value.id} className='flex items-center gap-2'>
                          <span className='text-primary-600'>•</span>
                          <Text>
                            {value.representativeFirstName} {value.representativeLastName}
                          </Text>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Text className='text-gray-400 italic'>No representatives assigned</Text>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <div className='bg-white rounded-xl shadow-md border border-gray-300 p-6 flex flex-col col-span-3'>
          <div className='flex items-center gap-2 mb-6'>
            <UserGroupIcon className='size-6 text-primary-600' />
            <Subheading>Staff Availability</Subheading>
            {isSaving && <Spinner size='sm' color='primary' />}
          </div>

          <StaffAvailabilityList
            staffMemberAvailabilities={staffMemberAvailabilities}
            accountId={data?.accountId}
            isEditable={userCanUpdateStaffAvailability}
            onStatusChange={setStaffMemberAvailabilityStatus}
            isLoading={staffMembersLoading}
            showPendingIndicator={true}
          />
        </div>
      </div>
    </div>
  );
};

export default DetailsAvailability;
